import type { Feed, FeedEntry } from '~/services/layout';
import { getClientFeedUrl } from '~/services/request/index.ts';
import { requestJsonData } from '~/utils/request-json-data';
import { QueueManager, setupClientQueue } from '~/services/queue/index.ts';

export type AdvanceQueueOnPlaybackEndedResult = {
  nextEntry: FeedEntry | null;
  nextQueueItems: FeedEntry[];
};

export type QueueTransportDirection = 'previous' | 'next';

export type ResolveAdjacentQueueEntryResult = {
  entry: FeedEntry | null;
  index: number;
  hasCurrentQueueItem: boolean;
};

function hasPlayableSource(entry: FeedEntry | undefined | null): boolean {
  return Boolean(entry?.content?.src || entry?.link?.href);
}

function getFeedEntryIndex({
  feed,
  entry,
}: {
  feed: Feed;
  entry: FeedEntry;
}): number {
  const feedEntries = Array.isArray(feed.entry) ? feed.entry : [];

  const entryIndexNumber =
    typeof entry._entryIndex === 'number'
      ? entry._entryIndex
      : Number(entry._entryIndex);

  if (
    Number.isInteger(entryIndexNumber) &&
    entryIndexNumber >= 0 &&
    entryIndexNumber < feedEntries.length
  ) {
    return entryIndexNumber;
  }

  if (!entry.id) {
    return -1;
  }

  return feedEntries.findIndex((feedEntry) => feedEntry.id === entry.id);
}

function getAdjacentIndex({
  index,
  direction,
}: {
  index: number;
  direction: QueueTransportDirection;
}): number {
  if (direction === 'previous') {
    return index - 1;
  }

  return index + 1;
}

/**
 * Resolves previous/next entry from current QueueManager queue context.
 */
export async function resolveAdjacentQueueEntry({
  entry,
  direction,
}: {
  queueItems?: FeedEntry[];
  entry?: FeedEntry;
  direction: QueueTransportDirection;
}): Promise<ResolveAdjacentQueueEntryResult> {
  setupClientQueue();
  if (entry) {
    await QueueManager.instance.onTrackStarted(entry);
  }

  if (direction === 'next') {
    const upcoming = await QueueManager.instance.getUpcomingQueue();
    const nextIndex = upcoming.findIndex(hasPlayableSource);
    const nextEntry = nextIndex !== -1 ? upcoming[nextIndex] : null;

    return {
      entry: nextEntry,
      index: nextIndex,
      hasCurrentQueueItem: Boolean(nextEntry),
    };
  }

  const history = await QueueManager.instance.getHistoryQueue();
  for (let i = history.length - 1; i >= 0; i--) {
    if (hasPlayableSource(history[i])) {
      return {
        entry: history[i],
        index: i,
        hasCurrentQueueItem: true,
      };
    }
  }

  return {
    entry: null,
    index: -1,
    hasCurrentQueueItem: false,
  };
}

/**
 * Resolves previous/next entry from source feed context using _computedUrl and _entryIndex.
 * Used as fallback when queue does not have adjacent entries.
 */
export async function resolveAdjacentSiblingEntry({
  entry,
  direction,
}: {
  entry: FeedEntry;
  direction: QueueTransportDirection;
}): Promise<FeedEntry | null> {
  const sourceFeedUrl =
    entry.extensions?.source_feed_url ||
    entry.extensions?.sourceFeedUrl ||
    (entry as Record<string, unknown>)._feedPath;

  const resolvedSourceFeedUrl =
    typeof sourceFeedUrl === 'string' && sourceFeedUrl.length > 0
      ? sourceFeedUrl
      : undefined;

  const rawComputedUrl = entry._computedUrl || resolvedSourceFeedUrl;

  if (typeof rawComputedUrl !== 'string' || rawComputedUrl.length === 0) {
    return null;
  }

  const clientFeedUrl = getClientFeedUrl(rawComputedUrl);

  if (!clientFeedUrl) {
    return null;
  }

  const sourceFeed = (await requestJsonData({
    url: clientFeedUrl,
  })) as Feed | undefined;

  if (!sourceFeed || !Array.isArray(sourceFeed.entry)) {
    return null;
  }

  const currentEntryIndex = getFeedEntryIndex({
    feed: sourceFeed,
    entry,
  });

  if (currentEntryIndex < 0) {
    return null;
  }

  const siblingEntryIndex = getAdjacentIndex({
    index: currentEntryIndex,
    direction,
  });

  if (siblingEntryIndex < 0 || siblingEntryIndex >= sourceFeed.entry.length) {
    return null;
  }

  const siblingEntry = sourceFeed.entry[siblingEntryIndex] ?? null;

  if (!siblingEntry?.content?.src) {
    return null;
  }

  return siblingEntry;
}

/**
 * Advances queue playback when the current audio item ends.
 * Reads the next upcoming item from QueueManager without destructive deletion.
 */
export async function advanceQueueOnPlaybackEnded({
  entry,
}: {
  entry: FeedEntry | null;
}): Promise<AdvanceQueueOnPlaybackEndedResult> {
  if (!entry?.id) {
    return {
      nextEntry: null,
      nextQueueItems: [],
    };
  }

  setupClientQueue();
  await QueueManager.instance.onTrackStarted(entry);
  const upcoming = await QueueManager.instance.getUpcomingQueue();
  const nextEntry = upcoming.find(hasPlayableSource) ?? null;

  return {
    nextEntry,
    nextQueueItems: upcoming,
  };
}
